/**
 * Pure utility functions for subtitle/caption logic.
 * No React dependencies — these are testable standalone.
 */

import type { Cue, Language } from "../definitions/types";

/**
 * Match browser language preferences against available caption languages.
 * Tries exact match first (e.g. "fr"), then prefix match (e.g. "en-US" → "en").
 */
export function detectBrowserLang(
  languages: Language[],
  fallback: string,
): string {
  const browserLangs = navigator.languages ?? [navigator.language];

  for (const bl of browserLangs) {
    const code = bl.toLowerCase();

    const exact = languages.find((l) => l.lang_code.toLowerCase() === code);
    if (exact) return exact.lang_code;

    const prefix = code.split("-")[0];
    const partial = languages.find((l) => l.lang_code.toLowerCase() === prefix);
    if (partial) return partial.lang_code;
  }

  return fallback;
}

/** Binary search for the cue active at a given time (seconds). */
export function findActiveIndex(cues: Cue[], time: number): number {
  let lo = 0;
  let hi = cues.length - 1;

  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    if (time < cues[mid].start) {
      hi = mid - 1;
    } else if (time >= cues[mid].end) {
      lo = mid + 1;
    } else {
      return mid;
    }
  }

  return -1;
}

/** Format seconds into "m:ss" timestamp. */
export function formatTimestamp(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds) % 60;
  return `${m}:${s < 10 ? "0" : ""}${s}`;
}

/** Base language of a caption code: "en-GB" → "en", "zh-CN" → "zh", "iw" → "he". */
export function baseLang(code: string): string {
  const base = code.toLowerCase().split(/[-_]/)[0];
  return base === "iw" ? "he" : base;
}

/**
 * Pairs each row cue with the text of the `other` track's cues that start
 * within its time span, so two translations line up on the same rows even
 * when one splits or merges lines. Cues starting a little early (0.5s) still
 * belong to the row they lead into.
 */
export function alignCues(rows: Cue[], other: Cue[]): string[] {
  const aligned: string[] = [];
  let j = 0;
  for (let i = 0; i < rows.length; i++) {
    const end = i + 1 < rows.length ? rows[i + 1].start - 0.5 : Infinity;
    const parts: string[] = [];
    while (j < other.length && other[j].start < end) {
      parts.push(other[j].text);
      j++;
    }
    aligned.push(parts.join(" "));
  }
  return aligned;
}
